<script setup>
import { Avatar } from '@dashhudson/dashing-ui/avatar';
import { PrimaryNavigationPanel } from '@dashhudson/dashing-ui/primary-navigation-panel';
import {
  SecondaryNavigationPanel,
  useSecondaryNavigationPanel,
} from '@dashhudson/dashing-ui/secondary-navigation-panel';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import NotificationsPanel from '@/components/layout/navigation/NotificationsPanel.vue';
import PrimaryNavigationMenu from '@/components/layout/navigation/PrimaryNavigationMenu.vue';
import UtilityPanel from '@/components/layout/navigation/UtilityPanel.vue';
import { branding } from '@/config';
import { useFlagStore } from '@/stores/flag';
import { trackNavigation, trackSecondaryNavigationInteractions } from '@/utils/analytics/tracking';

const flagStore = useFlagStore();

const props = defineProps({
  hideToggleButton: { type: Boolean, default: false },
});

const {
  toggleNavigationPanel,
  expanded: isExpanded,
  hidden: secondaryNavHidden,
} = useSecondaryNavigationPanel();

const route = useRoute();
const hideSecondaryNavigation = computed(() => {
  const hasAccessToSecondaryNavViaFeatureFlag =
    flagStore.ready && flagStore.flags?.[route?.meta?.showSecondaryNavFeatureFlagOverride];

  if (route?.meta?.showSecondaryNavFeatureFlagOverride)
    return secondaryNavHidden.value || !hasAccessToSecondaryNavViaFeatureFlag;
  return route?.meta?.hideSecondaryNav || secondaryNavHidden.value;
});

function handleSecondaryNavigationToggleClick() {
  toggleNavigationPanel();
  trackSecondaryNavigationInteractions(!isExpanded.value ? 'closed' : 'opened');
}
</script>

<template>
  <header
    class="dash-top-with-banner !fixed !bottom-0 !left-0 !grid !h-full !items-start !bg-white"
    :style="{ zIndex: 'var(--z-index-nav)', width: 'var(--primary-navigation-width)' }"
    data-primary-nav
  >
    <nav class="!grid !h-full !w-full !bg-white">
      <PrimaryNavigationPanel>
        <template #header>
          <router-link
            to="/"
            class="dash-rounded-sm focus-visible:dash-border focus-visible:dash-border-bd-inform focus-visible:dash-outline-none !flex !h-10 !w-8 !flex-col !items-center !justify-center"
            @click="trackNavigation('Landing Page')"
          >
            <span class="visually-hidden"> Go to the Dash Social home page </span>
            <Avatar :image="branding.iconPrimary" size="sm" class="!bg-white" />
          </router-link>
        </template>
        <PrimaryNavigationMenu
          class="dash-py-4"
          @active-item-click="handleSecondaryNavigationToggleClick"
        />
        <template #footer>
          <NotificationsPanel />
          <UtilityPanel />
        </template>
      </PrimaryNavigationPanel>
      <SecondaryNavigationPanel
        :hide="hideSecondaryNavigation"
        :hide-toggle-button="props.hideToggleButton"
        class="!left-[var(--primary-navigation-width)] -z-10 !w-[var(--secondary-navigation-width)]"
      />
    </nav>
  </header>
</template>

<style lang="postcss">
[data-primary-nav] {
  top: var(--dash-top-banner-offset, 0) !important;
  height: calc(100% - var(--dash-top-banner-offset, 0)) !important;
}

[data-secondary-nav] {
  top: var(--dash-top-banner-offset, 0) !important;
  height: calc(100% - var(--dash-top-banner-offset, 0)) !important;
}
</style>
